import { useAuth0 } from '@auth0/auth0-react';
import { BUTTON_PRIMARY, BUTTON_SECONDARY, PageHeader, peekReturnTo } from '../../shared/ui';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { environment } from '../../../environments/environment';

export function UnauthorizedPage() {
  const { error, loginWithRedirect } = useAuth0();

  return (
    <div className="mx-auto max-w-xl pl-4 pr-4">
      <PageHeader title="Unable to sign in" />
      <div className="rounded border-[1px] border-solid border-hilevel-red bg-hilevel-red/10 pl-4 pr-4 py-3">
        <p className="m-0 text-sm text-hilevel-slate">
          {error?.message ?? 'Your account does not have access to the distributor portal.'}
        </p>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="button" className={BUTTON_PRIMARY} onClick={() => loginWithRedirect()}>
          Try again
        </button>
        <Link to="/support" className={`${BUTTON_SECONDARY} no-underline`}>
          Support
        </Link>
      </div>
    </div>
  );
}

/**
 * The Auth0 redirect target.
 *
 * `@auth0/auth0-react` handles the callback itself before the app renders, so
 * by the time this shows there is nothing to do but get out of the way.
 *
 * It can still render after `main.tsx`'s `onRedirectCallback` has navigated:
 * React Router resolves that navigation before the tree re-renders at the new
 * location. This used to answer with a hard redirect to the dashboard, which
 * discarded `returnTo`. Reading the same target is what makes the order stop
 * mattering; the dashboard is the fallback for a sign-in that recorded
 * nowhere to go back to.
 */
export function CallbackPage() {
  const { isLoading, error } = useAuth0();

  if (!isLoading && !error) {
    return <Navigate to={peekReturnTo() ?? '/'} replace />;
  }

  return (
    <p className="m-8 text-center text-sm text-hilevel-gray">
      {error ? error.message : 'Signing you in…'}
    </p>
  );
}

export function NotFoundPage() {
  return (
    <div className="mx-auto max-w-xl pl-4 pr-4">
      <PageHeader title="Page not found" />
      <p className="m-0 pl-4 pr-4 text-sm text-hilevel-slate">
        That page does not exist.{' '}
        <Link to="/" className="text-hilevel-red">
          Back to the portal
        </Link>
        .
      </p>
    </div>
  );
}
